(() => {
  const config = window.SegalNameNecklace || {};
  const normalize = (value) => String(value || '').normalize('NFC').trim().toLocaleLowerCase('en-US').normalize('NFD').replace(/[̀-ͯ\s'’_-]/g, '');
  const sessionKey = 'segal-name-session-v2';
  // Stable per-VISITOR id. sessionStorage dies with the tab, so the Worker saw a new
  // "session" on every visit. Persist in localStorage, migrating any id this browser
  // already has, and tolerate storage being unavailable (private mode / blocked).
  const readStored = (store) => { try { return store.getItem(sessionKey) || ''; } catch { return ''; } };
  const writeStored = (store, value) => { try { store.setItem(sessionKey, value); } catch { /* storage blocked */ } };
  const newSessionId = () => (crypto.randomUUID ? crypto.randomUUID() : `sn-${Date.now()}-${Math.random().toString(16).slice(2)}`);
  const sessionId = readStored(localStorage) || readStored(sessionStorage) || newSessionId();
  writeStored(localStorage, sessionId);
  writeStored(sessionStorage, sessionId);

  const input = (config.inputSelectors || []).map((selector) => document.querySelector(selector)).find(Boolean);
  if (!input || !config.indexUrl) return;
  const form = input.closest('form') || document.querySelector('form[action*="/cart/add"]');
  const addButtons = () => [...(form?.querySelectorAll('[type="submit"], [name="add"]') || [])];
  const originalButtonLabels = new Map();
  addButtons().forEach((button) => originalButtonLabels.set(button, button.textContent));


  // Preserve the chosen chain across the real-page navigation used for a new name.
  // Infinite Options inserts this field asynchronously, so wait for its options.
  const chainArrivalKey = 'segal-name-chain-arrival-v1';
  const chainSelect = () => document.querySelector('select[name="properties[Chain length]"]');
  const rememberChain = (handle) => {
    try {
      const select = chainSelect();
      if (select) sessionStorage.setItem(chainArrivalKey, JSON.stringify({
        handle, value: select.value, at: Date.now()
      }));
      else sessionStorage.removeItem(chainArrivalKey);
    } catch { /* storage unavailable; normal selection remains available */ }
  };
  (() => {
    let arrival;
    try { arrival = JSON.parse(sessionStorage.getItem(chainArrivalKey) || 'null'); } catch { return; }
    if (!arrival) return;
    const clear = () => { try { sessionStorage.removeItem(chainArrivalKey); } catch {} };
    if (Date.now() - arrival.at > 300000 || !arrival.value) { clear(); return; }
    if (arrival.handle !== config.currentHandle) return;
    let attempts = 0;
    const restore = () => {
      const select = chainSelect();
      if (!select || !Array.from(select.options).some(option => option.value === arrival.value)) return false;
      select.value = arrival.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      clear();
      return true;
    };
    if (restore()) return;
    const timer = setInterval(() => {
      if (restore() || ++attempts >= 120) {
        clearInterval(timer);
        if (attempts >= 120) clear();
      }
    }, 250);
  })();

  let index = [];
  let activeHandle = config.currentHandle;
  let activeName = '';
  let activeProduct = { variants: config.currentVariants || [] };
  let pendingName = '';
  let requestPromise = null;
  const cacheKey = 'segal-name-index-v2';
  const productCache = new Map();
  const knownNameDelay = 250;
  // Raised from 900ms. The generation POST must never fire on a HALF-TYPED name: the
  // field is frozen for the whole job, so a request sent after `Ser` locks in `Ser`
  // while the customer is still typing `Seraphine` and they get a necklace they never
  // asked for. The create timer is armed INSIDE the knownNameDelay timer, so the
  // schedule below subtracts that to keep the TOTAL quiet time measured from the last
  // keystroke at exactly newNameDelay.
  const newNameDelay = 1500;
  // A single letter is never a settled name; it is the first keystroke of one.
  const MIN_NEW_NAME_LENGTH = 2;
  const metrics = window.SegalNameNecklaceMetrics = window.SegalNameNecklaceMetrics || {};
  let lastInputAt = performance.now();

  // "The last name the customer put in wins." Two separate facts must be tracked:
  //   latestName    - what the field holds RIGHT NOW, updated on every input event and
  //                   re-read from the DOM, because a paste/autofill can change the
  //                   value while the field is frozen without firing anything we saw.
  //   requestedName - the name actually sent to the Worker, captured at POST time.
  // They are compared at every terminal status: if they differ, the finished product is
  // for a name the customer has already moved on from and is never swapped in.
  let latestName = String(input.value || '').trim();
  let requestedName = '';
  // Runaway guard: a restart can itself be superseded. Never bounce more than
  // MAX_RESTARTS times in a row without a clean completion in between.
  const MAX_RESTARTS = 2;
  let restartCount = 0;

  const drawingLoader = (() => {
    const gallery = document.querySelector('.product-gallery__main, .product-gallery, .product-page__gallery, [data-product-gallery]');
    if (!gallery) return null;
    const style = document.createElement('style');
    style.textContent = `
      @keyframes segal-name-draw { 0% { stroke-dashoffset: 1200; fill: transparent; } 70% { stroke-dashoffset: 0; fill: transparent; } 100% { stroke-dashoffset: 0; fill: rgba(183,139,67,.14); } }
      @keyframes segal-chain-draw { 0% { stroke-dashoffset: 360; opacity: .2; } 55%,100% { stroke-dashoffset: 0; opacity: 1; } }
      @keyframes segal-name-glint { 0%,100% { transform: translateX(-22px); opacity: .25; } 50% { transform: translateX(22px); opacity: 1; } }
      .segal-name-drawing-host { position: relative !important; }
      .segal-name-drawing { position: absolute; inset: 0; z-index: 8; display: grid; place-items: center; pointer-events: none; background: rgba(255,255,255,.76); backdrop-filter: blur(2px); }
      .segal-name-drawing[hidden] { display: none; }
      .segal-name-drawing__card { width: min(88%, 620px); padding: 28px 18px 22px; text-align: center; color: #28231d; transform: translateY(-42px); }
      .segal-name-drawing__svg { display: block; width: 100%; height: auto; overflow: visible; }
      .segal-name-drawing__chain { fill: none; stroke: #b78b43; stroke-width: 2.1px; stroke-linecap: round; stroke-dasharray: 360; stroke-dashoffset: 360; animation: segal-chain-draw 2.4s ease-in-out infinite alternate; }
      .segal-name-drawing__text { font-family: inherit; font-size: 64px; line-height: 1; letter-spacing: 1px; stroke: #b78b43; stroke-width: 1.35px; stroke-linecap: round; stroke-linejoin: round; stroke-dasharray: 1200; stroke-dashoffset: 1200; animation: segal-name-draw 2.4s ease-in-out infinite alternate; }
      .segal-name-drawing__glint { display: inline-block; width: 42px; height: 1px; margin: 4px auto 13px; background: linear-gradient(90deg, transparent, #b78b43, transparent); animation: segal-name-glint 1.25s ease-in-out infinite; }
      .segal-name-drawing__status { display: block; font-size: 13px; letter-spacing: .09em; text-transform: uppercase; }
      .segal-name-drawing__hint { display: block; margin-top: 7px; font-size: 12px; letter-spacing: .04em; opacity: .72; }
      .segal-name-drawing__hint[hidden] { display: none; }
      @media (prefers-reduced-motion: reduce) { .segal-name-drawing__text, .segal-name-drawing__glint { animation-duration: 8s; } }
    `;
    document.head.appendChild(style);
    gallery.classList.add('segal-name-drawing-host');
    const overlay = document.createElement('div');
    overlay.className = 'segal-name-drawing';
    overlay.hidden = true;
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    overlay.innerHTML = '<div class="segal-name-drawing__card"><svg class="segal-name-drawing__svg" viewBox="0 0 600 165" aria-hidden="true"><path class="segal-name-drawing__chain" d="M 8 24 C 72 25, 98 56, 150 72"/><path class="segal-name-drawing__chain" d="M 592 24 C 528 25, 502 56, 450 72"/><text class="segal-name-drawing__text" x="300" y="112" text-anchor="middle"></text></svg><i class="segal-name-drawing__glint"></i><span class="segal-name-drawing__status">Your name necklace is being made…</span><span class="segal-name-drawing__hint" hidden></span></div>';
    gallery.appendChild(overlay);
    return {
      overlay,
      name: overlay.querySelector('.segal-name-drawing__text'),
      status: overlay.querySelector('.segal-name-drawing__status'),
      hint: overlay.querySelector('.segal-name-drawing__hint')
    };
  })();

  const showDrawing = (name, status, hint) => {
    if (!drawingLoader) return;
    drawingLoader.name.textContent = name;
    drawingLoader.status.textContent = status || `Your ${name} necklace is being made…`;
    if (drawingLoader.hint) {
      drawingLoader.hint.textContent = hint || '';
      drawingLoader.hint.hidden = !hint;
    }
    drawingLoader.overlay.hidden = false;
  };

  const hideDrawing = () => {
    if (drawingLoader) drawingLoader.overlay.hidden = true;
  };

  const busyStyle = document.createElement('style');
  busyStyle.textContent = `
    [data-sn-busy] { opacity: .62; cursor: progress; }
    .segal-name-message { display: block; margin: 8px 0 0; font-size: 13px; line-height: 1.45; color: #28231d; }
    .segal-name-message[hidden] { display: none; }
  `;
  document.head.appendChild(busyStyle);

  const setBusy = (busy, label = 'Creating your necklace…') => {
    input.setAttribute('aria-busy', String(busy));
    addButtons().forEach((button) => {
      // Never write textContent/innerText here: it destroys the theme's button markup
      // (cart SVG + hidden alternate labels). Cache the markup once, restore if anything
      // ever mangles it, and express "busy" purely through attributes + CSS.
      if (button.dataset.snOriginalHtml === undefined) button.dataset.snOriginalHtml = button.innerHTML;
      button.disabled = busy;
      button.setAttribute('aria-busy', String(busy));
      if (busy) {
        button.setAttribute('data-sn-busy', '');
        button.setAttribute('aria-label', label);
      } else {
        button.removeAttribute('data-sn-busy');
        button.removeAttribute('aria-label');
      }
      if (button.innerHTML !== button.dataset.snOriginalHtml) button.innerHTML = button.dataset.snOriginalHtml;
    });
  };

  const INTERIM_MESSAGE = "We don't have this name ready yet — contact us and we'll make it for you.";

  // While a generation job is in flight the spelling must not change underneath it:
  // the job was queued for THIS name. Freeze with `readonly`, NEVER `disabled` - a
  // disabled field is omitted from the cart form payload, which would silently strip
  // the Custom Name line-item property that production reads. The flag is authoritative:
  // the theme's own "Edit Name" toggle flips classes/attributes on this input, so the
  // gate observer below re-asserts readonly for as long as `frozen` is true.
  let frozen = false;
  // Unfreezing re-opens the gate, and the gate observer clears the visible message when
  // it opens. On a failure we unfreeze and then show the reason, so suppress exactly
  // that one clear - otherwise the customer would watch the error disappear instantly.
  let suppressGateClear = false;
  const setFrozen = (value) => {
    frozen = value;
    if (value) {
      input.readOnly = true;
      input.setAttribute('readonly', 'readonly');
      input.setAttribute('data-sn-frozen', '');
      return;
    }
    suppressGateClear = true;
    input.readOnly = false;
    input.removeAttribute('readonly');
    input.removeAttribute('data-sn-frozen');
    // MutationObserver callbacks are microtasks, so they run before this timeout.
    setTimeout(() => { suppressGateClear = false; }, 0);
  };

  let messageNode = null;
  const ensureMessageNode = () => {
    if (!messageNode || !messageNode.isConnected) {
      messageNode = document.createElement('p');
      messageNode.className = 'segal-name-message';
      messageNode.setAttribute('role', 'status');
      messageNode.setAttribute('aria-live', 'polite');
      messageNode.style.cssText = 'margin:8px 0 0;font-size:13px;line-height:1.45;color:#28231d;';
      // Place it on its own line under the name field row rather than inside that
      // row's nowrap flex container, which would squeeze the Edit Name link.
      const row = input.parentElement;
      if (row && row.parentElement) row.parentElement.insertBefore(messageNode, row.nextSibling);
      else (form || document.body).appendChild(messageNode);
    }
    return messageNode;
  };

  // Progress and errors share the one persistent status node, so the customer only ever
  // has a single place to look.
  const showMessage = (text) => {
    const node = ensureMessageNode();
    node.textContent = text;
    delete node.dataset.snError;
    node.hidden = false;
  };

  const showError = (message) => {
    stopProgress();
    hideDrawing();
    // Unfreeze before rendering the reason: no failure path may leave the field stuck.
    setFrozen(false);
    // Keep the field valid: a native validity bubble needs focus, auto-dismisses and
    // never actually renders here. Render a persistent node in the DOM instead.
    input.setCustomValidity('');
    showMessage(message || INTERIM_MESSAGE);
    ensureMessageNode().dataset.snError = 'true';
    setBusy(false);
  };

  const clearError = () => {
    input.setCustomValidity('');
    if (messageNode) messageNode.hidden = true;
  };

  // Wording that tracks the REAL job status rather than a generic spinner, in the
  // customer's own spelling and the metal they picked. Keys are exactly the Worker's
  // pending statuses; anything unmapped falls through and leaves the copy unchanged.
  const PROGRESS_COPY = {
    'queued': () => 'We are currently working on the design.',
    'generating': () => 'We are currently working on the design.',
    'quality-check': () => 'Checking the spelling and the chain…',
    'uploading': () => 'Almost ready — preparing your page…'
  };
  const GENERATING_HINT = 'This usually takes about 20 seconds.';
  const GENERATING_HINT_AFTER_MS = 8000;
  const readyMessage = (name) => `Your ${name} necklace is ready.`;

  // Repainted on a 1s ticker as well as on each poll, so the "about 20 seconds" line
  // appears close to the 8s mark instead of waiting for the next 1.5s poll.
  let progressState = null;
  let progressTimer = null;
  const paintProgress = () => {
    if (!progressState) return;
    const build = PROGRESS_COPY[progressState.status];
    if (!build) return;
    const line = build(progressState.name, progressState.metal);
    const hint = progressState.status === 'generating'
      && (performance.now() - progressState.since) >= GENERATING_HINT_AFTER_MS
      ? GENERATING_HINT : '';
    showDrawing(progressState.name, line, hint);
    showMessage(hint ? `${line} ${hint}` : line);
    setBusy(true, line);
  };
  const setProgress = (status, name, metal) => {
    if (!progressState || progressState.status !== status) {
      progressState = { status, name, metal, since: performance.now() };
    } else {
      progressState.name = name;
      progressState.metal = metal;
    }
    paintProgress();
    if (!progressTimer) progressTimer = setInterval(paintProgress, 1000);
  };
  function stopProgress() {
    if (progressTimer) clearInterval(progressTimer);
    progressTimer = null;
    progressState = null;
  }

  // The DOM value is the authority on "what the customer last put in". Input events
  // cover typing; re-reading here also catches a paste/autofill/programmatic write that
  // changed the field while it was frozen without firing an event we handled.
  const currentLatestName = () => {
    const live = String(input.value || '').trim();
    if (live !== latestName) latestName = live;
    return latestName;
  };

  const supersedes = (latest, requested) => !!requested && normalize(latest) !== normalize(requested);

  // Never restart for a value that is empty, is not yet a settled name, or that the
  // field's own native constraints reject.
  const isRestartable = (name) => {
    if (!name || name.length < MIN_NEW_NAME_LENGTH) return false;
    if (String(input.value || '').trim() === name && typeof input.checkValidity === 'function') {
      try { if (!input.checkValidity()) return false; } catch { /* no native validation here */ }
    }
    return true;
  };

  // The customer changed the spelling while a job was in flight. Thaw FIRST - no path
  // may leave the field stuck - then start a fresh flow for what they actually want:
  // an instant swap when that name is already in the cached index, a new generation
  // request otherwise.
  const restartFor = (name) => {
    stopProgress();
    hideDrawing();
    setFrozen(false);
    setBusy(false);
    if (restartCount >= MAX_RESTARTS) { showMessage(INTERIM_MESSAGE); return; }
    if (!isRestartable(name)) return;
    restartCount += 1;
    metrics.lastRestart = { name, count: restartCount, measuredAt: new Date().toISOString() };
    // Deferred by one macrotask so the in-flight requestName() runs its own `finally`
    // (which nulls requestPromise and unfreezes) BEFORE the new flow sets those up
    // again - otherwise that cleanup would immediately clobber the restart.
    setTimeout(() => {
      clearError();
      const entry = index.find((item) => item[0] === normalize(name));
      if (entry) { swapProduct(entry[1], name).catch((error) => showError(error.message)); return; }
      showDrawing(name);
      requestName(name).catch((error) => showError(error.message));
    }, 0);
  };

  // Every failure / rate-limit / rejection path ends here: thaw and show the reason
  // (showError does both), then honour the last name the customer typed.
  const failFlow = (message, requested) => {
    showError(message);
    const latest = currentLatestName();
    if (supersedes(latest, requested)) restartFor(latest);
  };

  metrics.state = () => ({ latestName, requestedName, frozen, restartCount, activeHandle, activeName });

  const loadIndex = async () => {
    const cached = localStorage.getItem(cacheKey);
    if (cached) index = JSON.parse(cached).names || [];
    const response = await fetch(config.indexUrl, { credentials: 'omit' });
    if (!response.ok) throw new Error('Name index unavailable');
    const payload = await response.json();
    index = payload.names || [];
    localStorage.setItem(cacheKey, JSON.stringify(payload));
  };

  const currentVariantTitle = () => {
    const currentId = String(form?.querySelector('[name="id"], [name="variant_id"]')?.value || '');
    return activeProduct.variants?.find((variant) => String(variant.id) === currentId)?.title
      || (config.currentVariants || []).find((variant) => String(variant.id) === currentId)?.title
      || activeProduct.variants?.[0]?.title;
  };

  // The Worker needs the metal the customer is actually looking at. The visible
  // "Metal Type:" control is the theme's single-option selector; the hidden
  // select[name="id"] carries variant ids as values but metal names as option text,
  // so either one yields the right label. Fall back to the catalogue default.
  const METAL_FALLBACK = '14K Yellow Gold';
  const currentMetal = () => {
    const selects = [
      ...((form || document).querySelectorAll('#variant-selector-option-0, select.single-option-selector, select[name="id"]')),
      ...document.querySelectorAll('#variant-selector-option-0, select.single-option-selector')
    ];
    for (const select of selects) {
      const label = String(select.selectedOptions?.[0]?.textContent || '').trim();
      if (label) return label;
    }
    const checked = document.querySelector('input[type="radio"][name*="etal"]:checked');
    return String(checked?.value || '').trim() || METAL_FALLBACK;
  };

  const syncVariant = () => {
    if (!form || !activeProduct?.variants?.length) return;
    const visibleChoice = [...form.querySelectorAll('select option:checked, input[type="radio"]:checked')]
      .map((element) => element.textContent || element.value).find((value) => activeProduct.variants.some((variant) => variant.title === value));
    const wanted = visibleChoice || currentVariantTitle();
    const variant = activeProduct.variants.find((item) => item.title === wanted) || activeProduct.variants[0];
    const idInput = form.querySelector('[name="id"], [name="variant_id"]');
    if (idInput && variant) idInput.value = variant.id;
    const shopifyVariantSelect = form.querySelector('select[name="id"]');
    if (shopifyVariantSelect && variant) shopifyVariantSelect.value = variant.id;
  };

  // Each slide carries its OWN aspect box: the theme renders an inline
  // `padding-top: <100/ratio>%` on the .rimage wrapper from that image's ratio, and
  // .rimage__img sits inside it at object-fit:fill. Swapping only the `src` leaves the
  // PREVIOUS product's box in place, so a 3:2 shot dropped into a 1:1 box renders
  // 608x608 instead of 608x405 - measured distortion 0.667, and 1.499 the other way.
  // Rewrite the box whenever the new image's ratio is known.
  const setRatio = (target, ratio) => {
    if (!target || !ratio || !isFinite(ratio) || ratio <= 0) return;
    const wrapper = target.closest ? target.closest('.rimage') : null;
    // Only touch wrappers the theme already sized this way; never invent a box.
    if (wrapper && wrapper.style && wrapper.style.paddingTop) {
      wrapper.style.paddingTop = `${100 / ratio}%`;
    }
  };

  const setImage = (target, src, alt, ratio) => {
    if (!target || !src) return;
    target.src = src;
    target.srcset = src;
    target.removeAttribute('data-master');
    target.alt = alt || '';
    setRatio(target, ratio);
  };

  // Slick (assets/plugin.slick.js) drives the main gallery with infinite:true, which
  // CLONES slides. DOM order of .product-gallery__main_item is therefore
  // [clones of the tail][real 0..n-1][clones of the head] - measured on this theme:
  // 12 real slides -> 25 nodes, with ONE leading clone. The old code walked that node
  // list in DOM order and wrote images[i] into node i, so the visible first slide
  // received images[1] while the name image landed in an invisible clone, and every
  // slide sat one image out of step with its thumbnail. That off-by-one IS the
  // "gallery jumped to another item" report. Resolve each node to the index of the
  // slide it actually represents instead, so clones get the same image as the slide
  // they mirror. Falls back to DOM order when there is no slider at all.
  const realIndexOf = (target, domIndex) => {
    const slide = target.closest ? target.closest('.slick-slide') : null;
    if (!slide) return domIndex;
    const raw = parseInt(slide.getAttribute('data-slick-index'), 10);
    if (Number.isNaN(raw)) return domIndex;
    const slider = slide.closest('.slick-slider');
    const count = slider ? slider.querySelectorAll('.slick-slide:not(.slick-cloned)').length : 0;
    if (!count) return raw;
    return ((raw % count) + count) % count;
  };

  // product.media runs parallel to product.images for image-type media and is the only
  // place the per-image dimensions are exposed to the storefront.
  const imageRatios = (product) => (product?.media || [])
    .filter((item) => item && item.media_type === 'image')
    .map((item) => item.aspect_ratio || (item.width && item.height ? item.width / item.height : 0));

  const GALLERY_SLIDERS = [
    '.product-gallery__main_slider',
    '.product-gallery__thumbnail_slider',
    '.product-gallery__fullscreen-thumbnail_slider'
  ];

  const slickApiFor = (selector) => {
    const element = document.querySelector(selector);
    if (!element || !element.classList.contains('slick-initialized')) return null;
    const api = element.slick;
    return api && typeof api.slickGoTo === 'function' ? api : null;
  };

  // After a swap the gallery must sit on slide 1 - the name image - and stay there.
  // Drive Slick through its OWN api rather than fighting it with CSS: slickGoTo(0,true)
  // jumps without animating, and setPosition re-measures the track now that every slide
  // has a new ratio (the main slider runs adaptiveHeight:true). Images decode
  // asynchronously and the theme's own scripts re-measure late, so re-assert over a
  // short window - but abort the moment the customer touches the gallery, so we never
  // trap them on slide 1.
  let settleToken = 0;
  // Which slide the gallery should come to rest on. 0 after a name swap (the name photo); the
  // colour's own slide after a metal image has just been generated, otherwise settleGallery's
  // late re-asserts would drag the customer straight back off the metal they picked.
  let settleTarget = 0;
  const settleGallery = () => {
    const token = ++settleToken;
    const main = document.querySelector('.product-gallery__main_slider');
    let cancelled = false;
    const stop = () => { cancelled = true; };
    const events = ['mousedown', 'touchstart', 'wheel', 'keydown'];
    events.forEach((type) => main?.addEventListener(type, stop, { once: true, passive: true }));
    const apply = () => {
      if (cancelled || token !== settleToken) return;
      GALLERY_SLIDERS.forEach((selector) => {
        const api = slickApiFor(selector);
        if (!api) return;
        try {
          if (api.currentSlide !== settleTarget) api.slickGoTo(settleTarget, true);
          api.setPosition();
        } catch (sliderBusy) { /* slick mid-transition: the next tick retries */ }
      });
    };
    apply();
    [120, 350, 800, 1500, 2500].forEach((delay) => setTimeout(apply, delay));
    setTimeout(() => {
      events.forEach((type) => main?.removeEventListener(type, stop));
    }, 3000);
  };

  // A product created moments ago carries ONE name photo - the metal the customer was
  // looking at. The other two colours are appended by the Worker's sweep a few minutes
  // later. The theme's own variant code maps a metal to a slide index using the media of
  // the product that was SERVER-rendered, so on a swapped-in product it lands on whatever
  // happens to sit at that index - measured: picking Rose on a freshly made name jumped
  // the gallery to slide 3, a lifestyle shot of a model. Resolve the slide from the
  // product actually on screen instead, and when that colour has not been generated yet
  // stay on the name photo rather than jumping to something that is not the necklace.
  const METAL_KEY = (metal) => /rose/i.test(metal) ? 'rose gold'
                  : /white|platinum/i.test(metal) ? 'white gold' : 'yellow gold';

  const metalSlideIndex = (metal) => {
    const images = activeProduct?.images || [];
    if (!images.length) return -1;
    const media = activeProduct?.media || [];
    const label = METAL_KEY(metal);
    // alt text is written by the Worker as "... in solid 14K White Gold, handmade in ..."
    for (let i = 0; i < images.length; i += 1) {
      if (String(media[i]?.alt || '').toLowerCase().includes(`solid 14k ${label},`)) return i;
    }
    // Fall back to the filename the Worker uploads: <productId>-14k-white-gold.jpg
    const slug = label.replace(' ', '-');
    for (let i = 0; i < images.length; i += 1) {
      if (String(images[i] || '').toLowerCase().includes(`-14k-${slug}`)) return i;
    }
    return -1;                       // this colour has not been generated yet
  };

  const goToSlide = (target) => {
    const apply = () => GALLERY_SLIDERS.forEach((selector) => {
      const api = slickApiFor(selector);
      if (!api) return;
      try { api.slickGoTo(target, true); api.setPosition(); } catch (sliderBusy) { /* next tick retries */ }
    });
    apply();
    // The theme re-runs its own variant handler after ours, so re-assert briefly.
    [80, 250, 600].forEach((delay) => setTimeout(apply, delay));
  };

  // A name created seconds ago only has the colour the customer first asked for; the other two
  // are produced by the Worker's sweep, which can be minutes away. Rather than leave them looking
  // at the wrong metal, ask the Worker to make that one colour now - a single recolour of the
  // photo they already approved, about fifteen seconds - and then move the gallery onto it.
  const colourEndpoint = () => {
    try { return `${new URL(config.requestUrl).origin}/name-colour`; } catch { return ''; }
  };
  let colourPending = '';
  // 2026-09-05 (mobile QA, diamond Celeste): the switcher now NAVIGATES to the new product the
  // moment it is ready, which is a few seconds before its white and rose photos exist - so the
  // server-rendered gallery has ONE slide. replaceGallery can only rewrite slides that exist, so
  // a colour at index 1 or 2 had nowhere to go and the yellow master stayed on screen for every
  // metal. A plain name never showed it (nine lifestyle slides absorb the extra photos). When the
  // photo's index is beyond the slides on the page, paint it onto the first slide instead.
  const galleryMainImages = () => [...document.querySelectorAll('.product-gallery__main_item img.rimage__img')];
  // "compressed": the page has fewer slides than the product has photos (rendered before the other
  // colours were added). Every metal then paints slot 0 - including yellow, once slot 0 has been
  // repainted with another colour.
  const compressedGallery = (product) => galleryMainImages().length < ((product && product.images) || []).length;
  const fitsGallery = (index, product) => index < 0 || (!compressedGallery(product || activeProduct) && galleryMainImages().length > index);
  const showOnFirstSlide = (product, index) => {
    const main = galleryMainImages()[0];
    const src = product?.images?.[index];
    if (!main || !src) return false;
    const ratios = imageRatios(product) || [];
    setImage(main, src, `${product.title} image ${index + 1}`, ratios[index] || ratios[0] || 0);
    const thumb = document.querySelector('.product-gallery__thumbnail_item img, .product-gallery__thumbnail img');
    if (thumb) setImage(thumb, src, `${product.title} thumbnail ${index + 1}`);
    settleTarget = 0;
    goToSlide(0);
    metrics.lastFirstSlidePaint = { index, src: String(src).split('/').pop().split('?')[0], measuredAt: new Date().toISOString() };
    return true;
  };
  const ensureMetalImage = async (metal) => {
    const endpoint = colourEndpoint();
    if (!endpoint || !activeHandle || colourPending === metal) return;
    colourPending = metal;
    // The colour is usually already on the product - it was derived seconds after the yellow
    // - and only this page's cached copy is stale. Re-read from the Worker (no edge cache)
    // before telling the customer anything; measured 2026-09-05: every metal switch flashed
    // "We are currently working on the design." and shifted the layout for a photo that
    // already existed.
    try {
      productCache.delete(activeHandle);
      const fresh = await fetchProduct(activeHandle, 1, true);
      if (fresh) {
        activeProduct = fresh;
        const have = metalSlideIndex(metal);
        if (have >= 0) {
          if (!fitsGallery(have, fresh)) { showOnFirstSlide(fresh, have); colourPending = ''; return; }
          settleTarget = have;
          replaceGallery(fresh.images, fresh.title, imageRatios(fresh));
          goToSlide(have);
          colourPending = '';
          return;
        }
      }
    } catch { /* fall through to the render */ }
    showMessage('We are currently working on the design.');
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ handle: activeHandle, metal })
      });
      const payload = await readJson(response);
      if (response.ok && payload && ['created', 'exists'].includes(payload.status)) {
        // The Worker answers as soon as Shopify ACCEPTS the new media, but the storefront JSON
        // does not list it for a few seconds after that. Reading once was too early - measured:
        // the rose photo existed on the product while the page still showed yellow. Re-read
        // until the colour actually appears, then swap.
        let product = null;
        let target = -1;
        for (let attempt = 0; attempt < 10; attempt += 1) {
          productCache.delete(activeHandle);
          product = await fetchProduct(activeHandle, 40, true);
          activeProduct = product;
          target = metalSlideIndex(metal);
          if (target >= 0) break;
          await new Promise((resolve) => setTimeout(resolve, 1500));
        }
        if (target >= 0) {
          if (!fitsGallery(target, product)) { showOnFirstSlide(product, target); }
          else {
            settleTarget = target;
            replaceGallery(product.images, product.title, imageRatios(product));
            goToSlide(target);
          }
        }
      }
    } catch (colourFailed) { /* the sweep will fill it in */ }
    finally { colourPending = ''; clearError(); }
  };

  const replaceGallery = (images, alt, ratios) => {
    if (!images?.length) return;
    const ratioAt = (index) => (ratios && ratios.length > index ? ratios[index] : 0);
    const mainImages = [...document.querySelectorAll('.product-gallery__main_item img.rimage__img')];
    mainImages.forEach((target, domIndex) => {
      const index = realIndexOf(target, domIndex);
      const src = images[index] || images[0];
      setImage(target, src, `${alt} image ${index + 1}`, images[index] ? ratioAt(index) : ratioAt(0));
    });
    const thumbnails = [...document.querySelectorAll('.product-gallery__thumbnail_item img, .product-gallery__thumbnail img')];
    // Thumbnails are object-fit:cover, so a ratio mismatch crops rather than stretches
    // and their boxes are deliberately left alone - only the index mapping is fixed.
    thumbnails.forEach((target, domIndex) => {
      const index = realIndexOf(target, domIndex);
      setImage(target, images[index] || images[0], `${alt} thumbnail ${index + 1}`);
    });
    if (!mainImages.length) {
      const fallback = ['.product-page__gallery img', '[data-product-featured-image]', '.product-single__photo img'].map((selector) => document.querySelector(selector)).find(Boolean);
      setImage(fallback, images[0], alt, ratioAt(0));
    }
    settleGallery();
  };

  // 2026-09-04, Yaniv: "just tried Helen and got failed". The product was created fine
  // (ACTIVE, yellow shot, 03:08:35); this step gave up. Two causes, both fixed here:
  //   1. cache:'no-store' only bypasses the BROWSER cache. The first read of a brand-new
  //      product's /products/x.js can be a 404 that Shopify's edge then serves back for the
  //      same URL, so twelve identical retries could all see the same cached 404. A unique
  //      query string per attempt is a different cache key.
  //   2. 12 x 1.5s = 18s is not always enough for a just-published product to resolve on
  //      the storefront under load. 40 x 1.5s = 60s is.
  // The Worker serves the same JSON shape from the Admin API with no edge cache. A product
  // created seconds ago answers a cached 404 (or a copy missing its newest colour) from
  // /products/x.js for up to ~30s; the Worker has it at once. Tried first when the caller says
  // the storefront copy is likely stale, otherwise only when the storefront copy is missing.
  const workerProduct = async (handle) => {
    try {
      const origin = new URL(config.requestUrl).origin;
      const r = await fetch(`${origin}/product/${encodeURIComponent(handle)}?_=${Date.now()}`, { cache: 'no-store' });
      return r.ok ? await r.json() : null;
    } catch { return null; }
  };
  const fetchProduct = async (handle, attempts = 40, preferWorker = false) => {
    if (productCache.has(handle)) return productCache.get(handle);
    if (preferWorker) {
      const w = await workerProduct(handle);
      if (w) { productCache.set(handle, w); return w; }
    }
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const bust = attempt === 0 ? '' : `?_=${Date.now()}`;
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js${bust}`, { credentials: 'same-origin', cache: 'no-store' });
      if (response.ok) {
        const product = await response.json();
        productCache.set(handle, product);
        return product;
      }
      const w = await workerProduct(handle);
      if (w) { productCache.set(handle, w); return w; }
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
    throw new Error('The necklace was created, but Shopify is still preparing its page. Please try again shortly.');
  };

  // 2026-09-05: swapping the product INTO the current page is gone. Measured across the
  // 10 scripts and the diamond family in one QA pass: a diamond name (3 photos) swapped into
  // a 12-slide gallery showed twelve copies of its yellow shot; a known name landed on the
  // yellow slide while the copy said "Shown in 14K Rose Gold"; the thumbnail strip stayed
  // scrolled to the old product; the breadcrumb, canonical, structured data and the theme's
  // own variant->image map were all still the page we started from. Every one of those is
  // a consequence of pretending the old page is the new product. The real product page has
  // none of them, so once the necklace exists we simply go there - with the variant for the
  // metal the customer is looking at, and `sn=ready` so name-necklace-ui.js opens on the
  // "your necklace is ready" block instead of the browse copy.
  const swapProduct = async (handle, requestedName) => {
    if (!handle) return;
    const swapStartedAt = performance.now();
    const wasCached = productCache.has(handle);
    const selectedTitle = currentVariantTitle();
    setBusy(true, 'Loading your necklace…');
    let product = null;
    try { product = await fetchProduct(handle, 12, true); } catch { product = null; }
    const metal = currentMetal();
    const metalOf = (title) => String(title || '').split('/')[0].trim();
    const variant = product?.variants?.find((item) => item.title === selectedTitle)
      || product?.variants?.find((item) => metalOf(item.title) === metal)
      || product?.variants?.[0];
    const next = new URL(`/products/${handle}`, location.origin);
    if (variant) next.searchParams.set('variant', variant.id);
    next.searchParams.set('sn', 'ready');
    try {
      sessionStorage.setItem('segal-name-arrival', JSON.stringify({ name: requestedName, handle, at: Date.now() }));
    } catch { /* storage blocked */ }
    metrics.lastReady = {
      path: wasCached ? 'existing-cached' : 'existing-network',
      name: requestedName,
      inputToReadyMs: Math.round(performance.now() - lastInputAt),
      swapMs: Math.round(performance.now() - swapStartedAt),
      navigatedTo: next.pathname + next.search,
      measuredAt: new Date().toISOString()
    };
    rememberChain(handle);
    location.assign(next.toString());
    // Keep the page frozen until the navigation lands; nothing below this runs on the new page.
    await new Promise(() => {});
  };

  // The job store moved to the Cloudflare Worker, which is a DIFFERENT origin from the
  // storefront - the old same-origin `${origin}/api/name-jobs/` path 404s forever there.
  // Prefer the shop metafield (config.jobUrl); fall back to the same-origin path only
  // when the metafield is missing.
  const jobUrlFor = (jobId) => {
    if (config.jobUrl) return `${String(config.jobUrl).replace(/\/+$/, '')}/${encodeURIComponent(jobId)}`;
    const base = new URL(config.requestUrl).origin;
    return `${base}/api/name-jobs/${encodeURIComponent(jobId)}`;
  };

  // Statuses that mean "still working, keep polling". `uploading` is emitted by the
  // Worker and behaves exactly like `generating`.
  const PENDING_STATUSES = ['queued', 'generating', 'uploading', 'quality-check'];
  const REVIEW_MESSAGE = 'This design needs a jewelry review before it can be purchased.';
  // `created-not-live` is a soft failure: the design exists but has no buyable page yet.
  const NOT_LIVE_MESSAGE = 'The necklace was created, but Shopify is still preparing its page. Please try again shortly.';

  // Bodies from the Worker are not guaranteed to be JSON (placeholder deploys, HTML
  // error pages from an edge). Never let a parse error escape as a raw exception.
  const readJson = async (response) => {
    const raw = await response.text();
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  };

  const poll = async (jobId, requestedName, requestStartedAt, metal, softFailures = 0) => {
    let response = null;
    try { response = await fetch(jobUrlFor(jobId), { cache: 'no-store' }); } catch { response = null; }
    const job = response ? await readJson(response) : null;
    if (!response || !response.ok || !job) {
      // Transport blip, non-JSON or error body. Tolerate a couple, then tell the
      // customer something rather than polling silently forever.
      if (softFailures >= 2) throw new Error((job && job.message) || INTERIM_MESSAGE);
      await new Promise((resolve) => setTimeout(resolve, 1500));
      return poll(jobId, requestedName, requestStartedAt, metal, softFailures + 1);
    }
    if (['unlisted-created', 'matched-existing'].includes(job.status) && job.product?.handle) {
      // Terminal status - the LAST name the customer put in wins. If the field moved on
      // while this job ran, the finished product is for a name they no longer want:
      // never swap it in. Thaw and start again for what they actually typed.
      // (`requestedName` here is this call's parameter - the name captured at POST time.)
      if (supersedes(currentLatestName(), requestedName)) {
        metrics.lastSuperseded = {
          requested: requestedName,
          latest: latestName,
          discardedHandle: job.product.handle,
          measuredAt: new Date().toISOString()
        };
        restartFor(latestName);
        return;
      }
      restartCount = 0;
      // Terminal success: let the customer read that it worked before the page changes.
      stopProgress();
      showDrawing(requestedName, readyMessage(requestedName));
      showMessage(readyMessage(requestedName));
      await new Promise((resolve) => setTimeout(resolve, 900));
      await swapProduct(job.product.handle, requestedName);
      metrics.lastReady = {
        path: job.status === 'unlisted-created' ? 'generated' : 'matched-existing',
        name: requestedName,
        inputToReadyMs: Math.round(performance.now() - lastInputAt),
        requestToReadyMs: Math.round(performance.now() - requestStartedAt),
        attempts: job.attempts,
        measuredAt: new Date().toISOString()
      };
      return;
    }
    if (['failed', 'human-review'].includes(job.status)) throw new Error(job.message || REVIEW_MESSAGE);
    // Soft failure: show the visible message, never swap the product.
    if (job.status === 'created-not-live') throw new Error(job.message || NOT_LIVE_MESSAGE);
    // A status this client does not know is not something we can ever resolve by
    // waiting - surface a message instead of looping forever.
    if (job.status && !PENDING_STATUSES.includes(job.status)) throw new Error(job.message || INTERIM_MESSAGE);
    // Pending: drive the wording off the Worker's real status.
    setProgress(job.status, requestedName, metal);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    return poll(jobId, requestedName, requestStartedAt, metal, 0);
  };

  const verifiedCustomerToken = async () => {
    if (!config.customerLoggedIn || !config.customerSessionUrl) return null;
    try {
      const url = new URL(config.customerSessionUrl, location.origin);
      url.searchParams.set('session', sessionId);
      const r = await fetch(url.toString(), { credentials: 'same-origin', cache: 'no-store', signal: AbortSignal.timeout(5000) });
      if (!r.ok) return null;
      const result = await r.json();
      return result.verified && typeof result.token === 'string' ? result.token : null;
    } catch { return null; } // Unverified visitors retain the guest allowance.
  };

  const requestName = async (name) => {
    if (!config.requestUrl) { showError(INTERIM_MESSAGE); return; }
    const normalized = normalize(name);
    if (requestPromise && pendingName === normalized) return requestPromise;
    pendingName = normalized;
    const metal = currentMetal();
    requestPromise = (async () => {
      const requestStartedAt = performance.now();
      setProgress('queued', name, metal);
      setBusy(true);
      // Capture what is actually being sent, at POST time.
      requestedName = name;
      let response;
      try {
        const customerToken = await verifiedCustomerToken();
        response = await fetch(config.requestUrl, {
          method: 'POST',
          // ONLY `content-type: application/json` may go on the wire here. The Worker
          // is a different origin, so this POST is preflighted; its
          // Access-Control-Allow-Headers does not list `x-segal-session`, so adding
          // that custom header made the OPTIONS preflight fail and fetch reject with
          // a TypeError BEFORE any POST was sent. The customer then got
          // INTERIM_MESSAGE from the catch below and the Worker never saw a request.
          // The session id already travels in the JSON body - keep it there only.
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ name, metal, session: sessionId, customerToken })
        });
      } catch (networkError) {
        // CORS / offline / DNS. The customer must never see nothing.
        failFlow(INTERIM_MESSAGE, name);
        return;
      }
      // The Worker answers 429/503 with {status, reason, message} on rate limits and
      // 400 {status:'rejected', reason, message} on blocked or invalid names. Surface
      // `message` verbatim through the persistent visible-message node instead of
      // throwing a raw error. A missing/non-JSON body falls back to the existing
      // interim copy.
      const payload = await readJson(response);
      // Rate limits (429/503) and rejections land here: thaw, show the reason, then
      // honour the last name the customer typed.
      if (!response.ok) { failFlow((payload && payload.message) || INTERIM_MESSAGE, name); return; }
      if (!payload) { failFlow(INTERIM_MESSAGE, name); return; }
      if (payload.status === 'rejected') { failFlow(payload.message || INTERIM_MESSAGE, name); return; }
      if (payload.status === 'known' && payload.entry?.handle) {
        // An index hit is terminal too, so the same last-name-wins rule applies.
        if (supersedes(currentLatestName(), name)) { restartFor(latestName); return; }
        restartCount = 0;
        return swapProduct(payload.entry.handle, name);
      }
      if (!payload.jobId) { failFlow(payload.message || INTERIM_MESSAGE, name); return; }
      // The request was accepted and a job exists: from here until a terminal status the
      // name is locked.
      setFrozen(true);
      try {
        return await poll(payload.jobId, name, requestStartedAt, metal);
      } catch (jobError) {
        // Same comparison after a failure: thaw + show the reason, then restart for the
        // last name if the field has moved on.
        failFlow((jobError && jobError.message) || INTERIM_MESSAGE, name);
        return;
      }
    })();
    try { return await requestPromise; }
    // Runs on success AND on every throw, so no path can leave the field frozen.
    finally { requestPromise = null; stopProgress(); setFrozen(false); }
  };

  let timer;
  let createTimer;
  const handleNameInput = () => {
    // Record the latest value BEFORE any early return. `readonly` blocks typing but not
    // a paste/autofill/programmatic write, and whatever the customer last put in the
    // field has to win even when it lands mid-job.
    latestName = String(input.value || '').trim();
    // Frozen means a job is in flight; rather than interrupting it here, latestName is
    // compared against the requested name when that job reaches a terminal status.
    if (frozen) return;
    // Idle and unfrozen: a fresh, human-driven session, so the runaway guard resets.
    if (!requestPromise) restartCount = 0;
    lastInputAt = performance.now();
    clearError();
    hideDrawing();
    clearTimeout(timer);
    clearTimeout(createTimer);
    timer = setTimeout(() => {
      const name = input.value.trim();
      const normalized = normalize(name);
      const entry = index.find((item) => item[0] === normalized);
      if (entry && entry[1] !== activeHandle) swapProduct(entry[1], name).catch((error) => showError(error.message));
      else if (entry) activeName = normalized;
      else if (name && name.length >= MIN_NEW_NAME_LENGTH && activeName !== normalized) {
        createTimer = setTimeout(() => {
          // Only ever fire for a name the field still holds.
          if (input.value.trim() !== name) return;
          showDrawing(name);
          if (!config.requestUrl) {
            setTimeout(() => showError(INTERIM_MESSAGE), 2500);
            return;
          }
          requestName(name).catch((error) => failFlow(error.message, name));
        }, Math.max(0, newNameDelay - knownNameDelay));
      }
    }, knownNameDelay);
  };

  // The theme ships the field as class="disabled" until the customer clicks the
  // "Edit Name" pencil (snippets/product-get-variants.liquid + module.product-page.js).
  // That gate is a feature and stays. The switcher just has to cooperate with it:
  //
  //  a) The direct listener below is bound at load, regardless of the class - a
  //     class alone never blocks input events, so as soon as the theme enables the
  //     field, typing drives the switcher with no re-binding needed.
  //  b) The delegated listener is the safety net for the case a class cannot cover:
  //     if the theme ever REPLACES the input node (options re-render), the direct
  //     binding dies with the old node while this one keeps working.
  //  c) The observer notices the moment the gate opens and clears any stale message,
  //     so clicking "Edit Name" is never a silent no-op.
  input.addEventListener('input', handleNameInput);

  const isNameField = (element) => !!element
    && (config.inputSelectors || []).some((selector) => { try { return element.matches(selector); } catch { return false; } });

  document.addEventListener('input', (event) => {
    if (event.target === input) return; // already handled by the direct listener
    if (!isNameField(event.target)) return;
    handleNameInput();
  }, true);

  const gateOpen = (element) => !element.disabled && !element.readOnly && !element.classList.contains('disabled');
  let wasGateOpen = gateOpen(input);
  new MutationObserver(() => {
    // The theme's "Edit Name"/"Save Name" toggle must not be able to unlock a field
    // that a running job depends on.
    if (frozen && !input.readOnly) {
      input.readOnly = true;
      input.setAttribute('readonly', 'readonly');
      return;
    }
    const openNow = gateOpen(input);
    if (openNow === wasGateOpen) return;
    wasGateOpen = openNow;
    if (openNow && !suppressGateClear) clearError();
  }).observe(input, { attributes: true, attributeFilter: ['class', 'disabled', 'readonly'] });

  // True when the product on screen carries the generated per-metal shots - an AI-built name,
  // whether this script swapped it in or the customer landed on its page directly. False for the
  // hand-built products, whose variant images the theme already maps correctly on its own.
  // The old test was "did this script swap the product in", which meant a customer who opened
  // /products/gold-name-necklace-<name> and changed metal moved the price and the label but never
  // the photo, and never triggered the colour to be generated.
  const hasGeneratedMetalShots = () =>
    (activeProduct?.media || []).some((item) => String(item?.alt || '').includes('name necklace in solid'));

  const onMetalChange = () => {
    syncVariant();
    if (hasGeneratedMetalShots()) {
      const metal = currentMetal();
      const slide = metalSlideIndex(metal);
      if (slide >= 0 && !fitsGallery(slide, activeProduct)) {
        // the photo exists but the page has no slide for it (see showOnFirstSlide)
        showOnFirstSlide(activeProduct, slide);
      } else {
        goToSlide(slide < 0 ? 0 : slide);
        // -1 means this colour has no photo yet: make it now instead of showing the wrong metal.
        if (slide < 0) ensureMetalImage(metal);
      }
      // The theme writes ?variant=<id> from the ORIGINAL product's variant list, which
      // leaves a stale id in the address bar - reloading or sharing that URL lands on a
      // variant that does not belong to this product. Correct it to the real one.
      const id = form?.querySelector('[name="id"], [name="variant_id"]')?.value;
      if (id) {
        const next = new URL(location.href);
        next.searchParams.set('variant', id);
        history.replaceState({}, '', next);
      }
    }
  };
  let lastSeenMetal = currentMetal();
  form?.addEventListener('change', () => setTimeout(() => { lastSeenMetal = currentMetal(); onMetalChange(); }, 0));
  // 2026-09-05 (mobile QA, diamond Ondine): the theme's "Metal Type" dropdown is a customSelect
  // that writes the hidden <select> and fires the change through jQuery's trigger(), which a
  // native listener never sees - measured: 0 native change events per pick. So the handler above
  // only ever ran for keyboard/native picks, and a fresh product's other colours were never
  // pulled into the gallery. name-necklace-ui.js polls the label for the same reason; do the same.
  setInterval(() => {
    const metal = currentMetal();
    if (metal === lastSeenMetal) return;
    lastSeenMetal = metal;
    onMetalChange();
  }, 300);
  form?.addEventListener('submit', (event) => {
    const name = input.value.trim();
    const entry = index.find((item) => item[0] === normalize(name));
    if (!name || activeName === normalize(name) || entry?.[1] === activeHandle) { activeName = normalize(name); syncVariant(); return; }
    event.preventDefault();
    const action = entry ? swapProduct(entry[1], name) : requestName(name);
    action.catch((error) => (entry ? showError(error.message) : failFlow(error.message, name)));
  }, true);

  loadIndex().catch(() => {});

  // The metal selector has to work on a name product the customer landed on DIRECTLY, which needs
  // that product's media - and the page never exposes it to JS. Read it once on load. Cheap: the
  // same /products/<handle>.js the switcher already uses, and it warms the cache for later swaps.
  if (activeHandle) {
    fetchProduct(activeHandle, 1)
      .then((product) => { activeProduct = product; })
      .catch(() => { /* leave the theme in charge */ });
  }
})();